import * as React from 'react';

import { useIsFetching } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { IconButton } from '~shared/components/buttons';
import {
  IconCopy,
  IconMerge,
  IconMove,
  IconTrash,
} from '~shared/components/icons/standard';
import { Inline } from '~shared/components/Inline';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { useWorkspaceList } from '~teacher/hook/workspaces';

import { itemTypeIs } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';

interface Props {
  openCopyQuizDialog: () => void;
  openMoveQuizDialog: () => void;
  deleteSelectedQuizFolder: () => void;
  openMergeQuizDialog: () => void;
  selectedQuizItem: Set<string>;
}

export function QuizLibraryActions({
  openMoveQuizDialog,
  deleteSelectedQuizFolder,
  openMergeQuizDialog,
  selectedQuizItem,
  openCopyQuizDialog,
}: Props) {
  const { data: hasSharedLibraries } = useWorkspaceList({
    select: (workspaces) => workspaces.length > 0,
  });

  const [isActionsDisabled, setIsActionsDisabled] = React.useState(false);
  const temporarilyDisableActions = () => {
    setIsActionsDisabled(true);
    setTimeout(() => setIsActionsDisabled(false), 400);
  };
  const isFetching = useIsFetching();

  const isLessThanOneItemSelected = selectedQuizItem.size <= 0;
  const isMoreThanFiftyItemSelected = selectedQuizItem.size >= 50;
  const isFolderSelected = [...selectedQuizItem].some(itemTypeIs('folder'));
  const isNotExactlyTwoItems = selectedQuizItem.size !== 2;

  const isCopyToWorkspaceDisabled =
    !hasSharedLibraries ||
    isLessThanOneItemSelected ||
    isMoreThanFiftyItemSelected ||
    isFolderSelected ||
    isFetching !== 0 ||
    isActionsDisabled;

  const isMoveQuizDisabled =
    isLessThanOneItemSelected || isFetching !== 0 || isActionsDisabled;

  const isMergeQuizDisabled =
    isFolderSelected ||
    isNotExactlyTwoItems ||
    isFetching !== 0 ||
    isActionsDisabled;

  const isDeleteDisabled =
    isLessThanOneItemSelected || isFetching !== 0 || isActionsDisabled;

  return (
    <Inline>
      <TooltipV2 isDisabled={isDeleteDisabled} label={translate('Delete')}>
        <IconButton
          appearance="default"
          aria-label={translate('Delete')}
          disabled={isDeleteDisabled}
          onClick={() => {
            temporarilyDisableActions();
            deleteSelectedQuizFolder();
          }}
        >
          <IconTrash />
        </IconButton>
      </TooltipV2>
      <TooltipV2 isDisabled={isMergeQuizDisabled} label={translate('Merge')}>
        <IconButton
          appearance="default"
          aria-label={translate('Merge')}
          onClick={() => {
            temporarilyDisableActions();
            openMergeQuizDialog();
          }}
          disabled={isMergeQuizDisabled}
        >
          <IconMerge />
        </IconButton>
      </TooltipV2>
      <TooltipV2 isDisabled={isMoveQuizDisabled} label={translate('Move')}>
        <IconButton
          appearance="default"
          aria-label={translate('Move')}
          onClick={() => {
            temporarilyDisableActions();
            openMoveQuizDialog();
          }}
          disabled={isMoveQuizDisabled}
        >
          <IconMove />
        </IconButton>
      </TooltipV2>
      <TooltipV2
        isDisabled={isCopyToWorkspaceDisabled}
        label={translate('Copy To Shared Library')}
      >
        <IconButton
          appearance="default"
          aria-label={translate('Copy To Shared Library')}
          onClick={() => {
            temporarilyDisableActions();
            openCopyQuizDialog();
          }}
          disabled={isCopyToWorkspaceDisabled}
        >
          <IconCopy />
        </IconButton>
      </TooltipV2>
    </Inline>
  );
}
